import React, { useState } from "react";
import Seo from "@/components/Seo";
import { SEO } from "@/lib/seoConfig";
import { useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Section, Kicker, SerifTitle } from "@/components/Section";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { ChevronDown, Plus } from "lucide-react";
import { BUDGETS, HEAR_ABOUT, SESSION_TYPES, LOCATION_OPTIONS, INQUIRY_CONFIRMATION, PRICING_BOOKING_NOTE } from "@/lib/data";
import { PublicAPI } from "@/lib/api";
import { cn } from "@/lib/utils";

export default function Inquire() {
    const [params] = useSearchParams();
    const initialSessionType = params.get("type") || "";
    const [form, setForm] = useState({
        full_name: "",
        email: "",
        phone: "",
        session_type: initialSessionType,
        desired_date: "",
        location: "",
        vision: "",
        budget_range: "",
        media_type: "",
        image_usage: "",
        inspiration_url: "",
        hear_about: "",
    });
    const [showMore, setShowMore] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const onChange = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

    const submit = async (e) => {
        e.preventDefault();
        // Required: name, email, session_type, desired_date, location, vision
        if (!form.full_name || !form.email) {
            toast.error("Please add your name and email.");
            return;
        }
        if (!form.session_type) {
            toast.error("Please choose what you're interested in.");
            return;
        }
        try {
            setSubmitting(true);
            const cleanedForm = Object.fromEntries(
                Object.entries(form).map(([k, v]) => [k, v === "" ? null : v])
            );
            await PublicAPI.createInquiry(cleanedForm);
            setSubmitted(true);
            toast.success("Inquiry received — we will respond with next steps.");
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (err) {
            toast.error("Something went wrong. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="pt-24 sm:pt-28">
            <Seo {...SEO.inquire} />
            <Section spacing="tight">
                <Kicker>Request Availability</Kicker>
                <SerifTitle className="mt-3 max-w-[22ch]">Tell me what you&rsquo;re planning.</SerifTitle>
                <p className="mt-5 max-w-[64ch] text-sm leading-[1.85] text-muted-foreground">
                    Share a few details below and I&rsquo;ll respond with availability, recommendations, and next steps within 1&ndash;2 business days.
                </p>
            </Section>

            <Section spacing="normal">
                {submitted ? (
                    <div data-testid="inquiry-form-success-message" className="max-w-[760px] mx-auto border border-[hsl(var(--lux-brass))]/40 bg-[hsl(var(--lux-porcelain))] p-10 sm:p-14 rounded-[2px] text-center">
                        <Kicker>Thank you</Kicker>
                        <SerifTitle className="mt-3">Your inquiry has been received.</SerifTitle>
                        <p className="mt-5 text-sm text-muted-foreground max-w-[60ch] mx-auto">
                            {INQUIRY_CONFIRMATION}
                        </p>
                        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                            <Link to="/portfolio">
                                <Button variant="ghost" className="rounded-full px-5 h-10 text-[11px] tracking-[0.22em] uppercase border border-border">View portfolio</Button>
                            </Link>
                            <Link to="/journal">
                                <Button variant="ghost" className="rounded-full px-5 h-10 text-[11px] tracking-[0.22em] uppercase border border-border">Read the journal</Button>
                            </Link>
                        </div>
                    </div>
                ) : (
                    <form data-testid="inquiry-form" onSubmit={submit} className="max-w-[820px] mx-auto bg-[hsl(var(--lux-porcelain))] border border-border rounded-[2px] p-7 sm:p-10">
                        {/* Required fields */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <Field label="Full name" required htmlFor="inquire-full_name">
                                <Input id="inquire-full_name" name="full_name" autoComplete="name" data-testid="inquiry-field-name" required value={form.full_name} onChange={onChange("full_name")} />
                            </Field>
                            <Field label="Email" required htmlFor="inquire-email">
                                <Input id="inquire-email" name="email" type="email" autoComplete="email" data-testid="inquiry-field-email" required value={form.email} onChange={onChange("email")} />
                            </Field>
                            <Field label="What are you interested in?" required className="sm:col-span-2" htmlFor="inquire-session_type">
                                <Select value={form.session_type} onValueChange={(v) => setForm({ ...form, session_type: v })} name="session_type">
                                    <SelectTrigger id="inquire-session_type" data-testid="inquiry-field-session-type"><SelectValue placeholder="Choose one" /></SelectTrigger>
                                    <SelectContent>
                                        {SESSION_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </Field>
                            <Field label="Preferred date or month" htmlFor="inquire-desired_date">
                                <Input id="inquire-desired_date" name="desired_date" data-testid="inquiry-field-date" placeholder="Example: June 14 evening / sometime in July / flexible" value={form.desired_date} onChange={onChange("desired_date")} />
                            </Field>
                            <Field label="Location" htmlFor="inquire-location">
                                <Select value={form.location} onValueChange={(v) => setForm({ ...form, location: v })} name="location">
                                    <SelectTrigger id="inquire-location" data-testid="inquiry-field-location"><SelectValue placeholder="Choose one" /></SelectTrigger>
                                    <SelectContent>
                                        {LOCATION_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </Field>
                        </div>

                        <div className="mt-5">
                            <Field label="Tell me what you’re hoping to create" htmlFor="inquire-vision">
                                <Textarea id="inquire-vision" name="vision" data-testid="inquiry-field-vision" rows={4} placeholder="Share a little about the people, the moment, the mood, or the goal of the session." value={form.vision} onChange={onChange("vision")} />
                            </Field>
                        </div>

                        {/* Progressive disclosure: optional fields */}
                        <div className="mt-6">
                            <button
                                type="button"
                                onClick={() => setShowMore((v) => !v)}
                                data-testid="inquiry-toggle-more"
                                aria-expanded={showMore}
                                className="inline-flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase text-muted-foreground hover:text-foreground"
                            >
                                {showMore ? <ChevronDown className="h-3.5 w-3.5 rotate-180 transition-transform" /> : <Plus className="h-3.5 w-3.5" />}
                                {showMore ? "Hide additional details" : "Add more details"}
                            </button>
                            <AnimatePresence initial={false}>
                                {showMore && (
                                    <motion.div
                                        key="more-details"
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: "auto", opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                                        className="overflow-hidden"
                                    >
                                        <Separator className="my-6 bg-border" />
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="inquiry-optional-fields">
                                            <Field label="Phone number (optional)" htmlFor="inquire-phone">
                                                <Input id="inquire-phone" name="phone" type="tel" autoComplete="tel" data-testid="inquiry-field-phone" value={form.phone} onChange={onChange("phone")} />
                                            </Field>
                                            <Field label="Estimated investment range (optional)" htmlFor="inquire-budget_range">
                                                <Select value={form.budget_range} onValueChange={(v) => setForm({ ...form, budget_range: v })} name="budget_range">
                                                    <SelectTrigger id="inquire-budget_range" data-testid="inquiry-field-budget"><SelectValue placeholder="Choose one" /></SelectTrigger>
                                                    <SelectContent>
                                                        {BUDGETS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                                    </SelectContent>
                                                </Select>
                                            </Field>
                                            <Field label="Photo, video, or both? (optional)" htmlFor="inquire-media_type">
                                                <Select value={form.media_type} onValueChange={(v) => setForm({ ...form, media_type: v })} name="media_type">
                                                    <SelectTrigger id="inquire-media_type" data-testid="inquiry-field-media"><SelectValue placeholder="Choose one" /></SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="photo">Photo</SelectItem>
                                                        <SelectItem value="video">Video</SelectItem>
                                                        <SelectItem value="both">Both</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </Field>
                                            <Field label="Where will the images be used? (optional)" htmlFor="inquire-image_usage">
                                                <Input id="inquire-image_usage" name="image_usage" data-testid="inquiry-field-usage" placeholder="Personal, website, social, print, campaign, event…" value={form.image_usage} onChange={onChange("image_usage")} />
                                            </Field>
                                            <Field label="Inspiration / moodboard link (optional)" htmlFor="inquire-inspiration_url">
                                                <Input id="inquire-inspiration_url" name="inspiration_url" type="url" inputMode="url" data-testid="inquiry-field-inspiration" placeholder="https://" value={form.inspiration_url} onChange={onChange("inspiration_url")} />
                                            </Field>
                                            <Field label="How did you hear about me? (optional)" htmlFor="inquire-hear_about">
                                                <Select value={form.hear_about} onValueChange={(v) => setForm({ ...form, hear_about: v })} name="hear_about">
                                                    <SelectTrigger id="inquire-hear_about" data-testid="inquiry-field-hear-about"><SelectValue placeholder="Choose one" /></SelectTrigger>
                                                    <SelectContent>
                                                        {HEAR_ABOUT.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                                    </SelectContent>
                                                </Select>
                                            </Field>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <p className="text-xs text-muted-foreground max-w-[44ch]">
                                A limited number of sessions and commissions are accepted each month.
                            </p>
                            <button
                                data-testid="inquiry-form-submit-button"
                                type="submit"
                                disabled={submitting}
                                className="btn-request"
                            >
                                {submitting ? "Sending…" : "REQUEST AVAILABILITY"}
                            </button>
                        </div>
                    </form>
                )}

                {/* Booking note */}
                {!submitted && (
                    <div className="max-w-[820px] mx-auto mt-6 text-xs text-muted-foreground leading-[1.85] px-1">
                        <span className="kicker mr-2">Booking</span>
                        {PRICING_BOOKING_NOTE}
                    </div>
                )}
            </Section>
        </div>
    );
}

const Field = ({ label, children, required, className, htmlFor }) => (
    <div className={cn("block", className)}>
        <label htmlFor={htmlFor} className="kicker block mb-2 cursor-pointer">
            {label}
            {required && <span className="ml-1 text-[hsl(var(--lux-oxblood))]">*</span>}
        </label>
        {children}
    </div>
);
